import {Suspense} from 'react'
import {Outlet, useLocation} from 'react-router-dom'
import {I18nProvider} from '../_metronic/i18n/i18nProvider'
import {LayoutProvider, LayoutSplashScreen} from '../_metronic/layout/core'
import {MasterInit} from '../_metronic/layout/MasterInit'
// import {AuthInit} from './modules/auth'
import {DataProvider} from './UI/DataContext'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {Provider} from 'react-redux'
import {store} from './redux/store'
import {usePushNotifications} from '../hooks/usePushNotifications'
import {useKeepAwake} from '../hooks/useKeepAwake'
import {IncomingCallProvider, useIncomingCall} from '../hooks/useIncomingCall'
import {IncomingCallModal} from '../components/IncomingCallModal'
import {ActiveCallModal} from '../components/ActiveCallModal'
import {ConsumerWidgetProvider} from './context/ConsumerWidgetContext'
import {useSetAgentStatusMutation} from './redux/agentStatusApi'
import {useAuth} from './modules/auth'
import {PresenceProvider} from './context/AWSConnectEventsContext'
import {GlobalDataProvider} from './providers/GlobalDataProvider'
import ConnectWidget from '../components/ConnectStreamsWidget/ConnectStreamsWidget'
import {OfflineWarningModal} from '../components/OfflineWarningModal'
import TokenUpdateModal from '../components/TokenUpdateModal'
import {ConnectStreamsProvider} from './context/ConnectStreamsContext'
import CookieConsentBanner from '../components/CookieConsentBanner/CookieConsentBanner'
import {useCookieSync} from '../hooks/useCookieSync'
import ConnectionTestGate from './UI/connection-test/ConnectionTestGate'
import {PendingFeedbackGate} from './modules/ai-interpretation/PendingFeedbackGate'
import {isWidgetFreeRoute} from './routing/widgetFreeRoutes'

const AppContent = () => {
  const {
    showModal,
    callData,
    isStandby,
    activeCalls,
    callGuardrails,
    acceptCall,
    rejectCall,
    hideIncomingCall,
    removeActiveCall,
  } = useIncomingCall()
  const {currentUser} = useAuth()
  const {pathname} = useLocation()
  const isInterpreter = currentUser?.result?.userType === 'INTERPRETER'
  const hideFloatingWidgets = isWidgetFreeRoute(pathname)
  const pendingPreCall = callGuardrails.find(
    (record) => record.hasPreCallQuestions && !record.preCallSubmitted
  )
  const visibleActiveCall =
    activeCalls.find((call) => call.callHeaderId === pendingPreCall?.callHeaderId) ??
    activeCalls[0]

  // Initialize push notifications
  const pushNotifications = usePushNotifications()

  useKeepAwake(isInterpreter)

  // Sync user data & permissions into cookies based on consent
  useCookieSync()

  // Inactivity detection
  const [setAgentStatus] = useSetAgentStatusMutation()

  return (
    <GlobalDataProvider>
    <DataProvider>
      <ConsumerWidgetProvider>
        <ToastContainer
          position='top-center'
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        <Suspense fallback={<LayoutSplashScreen />}>
          <I18nProvider>
            <LayoutProvider>
              <Outlet />
              <MasterInit />
              {/* </AuthInit> */}
            </LayoutProvider>
          </I18nProvider>
        </Suspense>

        {/* Incoming Call Modal */}
        <IncomingCallModal
          show={showModal}
          callData={callData}
          isStandby={isStandby}
          onAccept={acceptCall}
          onReject={rejectCall}
          onClose={hideIncomingCall}
        />

        {/* Active Call Modal — keyed by callHeaderId so contactId churn does not remount */}
        {visibleActiveCall && (
          <ActiveCallModal
            key={visibleActiveCall.callHeaderId}
            callData={visibleActiveCall}
            onClose={() => removeActiveCall(visibleActiveCall.callId)}
          />
        )}

        {isInterpreter && !hideFloatingWidgets && <ConnectWidget />}

        {isInterpreter && <OfflineWarningModal />}

        {isInterpreter && <TokenUpdateModal />}

        {isInterpreter && <ConnectionTestGate />}

        {currentUser && <PendingFeedbackGate />}

        <CookieConsentBanner />
      </ConsumerWidgetProvider>
    </DataProvider>
    </GlobalDataProvider>
  )
}

const PresenceWrapper: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {currentUser} = useAuth()
  const isSystemUser = currentUser?.result?.userType === 'SYSTEM'
  const isInterpreter = currentUser?.result?.userType === 'INTERPRETER'

  if (isSystemUser) {
    return <PresenceProvider>{children}</PresenceProvider>
  }

  if (isInterpreter) {
    return (
      <ConnectStreamsProvider>
      <IncomingCallProvider>{children}</IncomingCallProvider>
      </ConnectStreamsProvider>
    )
  }

  return <>{children}</>
}

const App = () => {
  return (
    <Provider store={store}>
      <PresenceWrapper>
        <AppContent />
      </PresenceWrapper>
    </Provider>
  )
}

export default App
